import React from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Line,
  LineChart,
  Pie,
  PieChart,
  XAxis,
  YAxis,
} from 'recharts';
import { AlertCircle } from 'lucide-react';
import { ChartContainer, ChartTooltip, ChartTooltipContent } from '../../../ui/chart';
import type { DashboardAnalyticsResultPayload } from '../types';

const CHART_COLORS = ['#6B8E6B', '#D79B61', '#536B79', '#A97862', '#8B7E66', '#334155'];

export function DashboardAnalyticsResultMessage({
  payload,
}: {
  payload: DashboardAnalyticsResultPayload;
}) {
  const rows = Array.isArray(payload.rows) ? payload.rows : [];
  const visualization = payload.visualization || { type: 'table' as const };
  const seriesList = visualization.series?.length
    ? visualization.series
    : [{ key: 'value', label: 'Value', format: 'number' as const }];
  const primarySeries = seriesList[0] || { key: 'value', label: 'Value', format: 'number' as const };
  const valueKey = primarySeries.key || 'value';
  const xKey = visualization.xKey || 'name';
  const chartConfig = seriesList.reduce((config, item, index) => {
    const key = item.key || 'value';
    config[key] = {
      label: item.label || 'Value',
      color: CHART_COLORS[index % CHART_COLORS.length],
    };
    return config;
  }, {} as Record<string, { color: string; label: string }>);
  const seriesFormatByKey = seriesList.reduce((formats, item) => {
    formats[item.key || 'value'] = item.format;
    return formats;
  }, {} as Record<string, 'currency' | 'number' | 'percent' | undefined>);

  return (
    <div
      className="w-full overflow-hidden rounded-lg border border-edge bg-white text-sm shadow-sm"
      data-analytics-title={payload.title || 'Dashboard analytics'}
      data-analytics-visualization={visualization.type}
      data-testid="dashboard-analytics-result"
    >
      <div className="border-b border-edge/70 px-4 py-3">
        <div className="font-semibold leading-5 text-ink">{payload.title || 'Dashboard analytics'}</div>
        {payload.subtitle ? (
          <div className="mt-1 text-xs leading-4 text-ink-muted">{payload.subtitle}</div>
        ) : null}
      </div>
      <div className="space-y-4 px-4 py-4">
        {rows.length === 0 ? (
          <div className="rounded-md border border-dashed border-edge px-3 py-6 text-center text-sm text-ink-muted">
            No matching analytics data found.
          </div>
        ) : visualization.type === 'kpi' ? (
          <KpiResult rows={rows} seriesLabel={primarySeries.label || 'Value'} valueKey={valueKey} format={primarySeries.format} />
        ) : visualization.type === 'line' ? (
          <ChartContainer className="h-56 w-full aspect-auto" config={chartConfig}>
            <LineChart data={rows} margin={{ left: 4, right: 12, top: 12 }}>
              <CartesianGrid vertical={false} />
              <XAxis dataKey={xKey} tickLine={false} axisLine={false} minTickGap={24} />
              <YAxis tickLine={false} axisLine={false} width={48} tickFormatter={(value) => formatAxisValue(value, primarySeries.format)} />
              <ChartTooltip
                content={<ChartTooltipContent formatter={(value, name) => formatTooltipValue(value, seriesFormatByKey[String(name)] || primarySeries.format)} />}
              />
              {seriesList.map((item) => {
                const key = item.key || 'value';
                return (
                  <Line
                    key={key}
                    type="monotone"
                    dataKey={key}
                    stroke={`var(--color-${key})`}
                    strokeWidth={2}
                    dot={false}
                  />
                );
              })}
            </LineChart>
          </ChartContainer>
        ) : visualization.type === 'bar' ? (
          <ChartContainer className="h-56 w-full aspect-auto" config={chartConfig}>
            <BarChart data={rows} margin={{ left: 4, right: 12, top: 12 }}>
              <CartesianGrid vertical={false} />
              <XAxis dataKey={xKey} tickLine={false} axisLine={false} minTickGap={16} />
              <YAxis tickLine={false} axisLine={false} width={48} tickFormatter={(value) => formatAxisValue(value, primarySeries.format)} />
              <ChartTooltip
                content={<ChartTooltipContent formatter={(value, name) => formatTooltipValue(value, seriesFormatByKey[String(name)] || primarySeries.format)} />}
              />
              {seriesList.map((item) => {
                const key = item.key || 'value';
                return (
                  <Bar
                    key={key}
                    dataKey={key}
                    fill={`var(--color-${key})`}
                    radius={[4, 4, 0, 0]}
                  />
                );
              })}
            </BarChart>
          </ChartContainer>
        ) : visualization.type === 'pie' ? (
          <ChartContainer className="h-56 w-full aspect-auto" config={chartConfig}>
            <PieChart>
              <ChartTooltip content={<ChartTooltipContent formatter={(value) => formatTooltipValue(value, primarySeries.format)} />} />
              <Pie
                data={rows}
                dataKey={valueKey}
                nameKey={xKey}
                innerRadius={54}
                outerRadius={82}
                paddingAngle={2}
              >
                {rows.map((_, index) => (
                  <Cell key={`analytics-slice-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                ))}
              </Pie>
            </PieChart>
          </ChartContainer>
        ) : null}

        {visualization.type !== 'table' && rows.length > 0 ? (
          <AnalyticsRowsTable
            rows={rows}
            seriesList={seriesList}
            xKey={xKey}
          />
        ) : rows.length > 0 ? (
          <AnalyticsRowsTable
            rows={rows}
            seriesList={seriesList}
            xKey={xKey}
          />
        ) : null}

        {payload.insights?.length ? (
          <div className="space-y-1 text-xs leading-5 text-ink-muted">
            {payload.insights.map((insight) => (
              <div key={insight}>{insight}</div>
            ))}
          </div>
        ) : null}

        {payload.caveats?.length ? (
          <div className="space-y-1 rounded-md bg-paper-light px-3 py-2 text-xs leading-5 text-ink-muted">
            {payload.caveats.map((caveat) => (
              <div key={caveat} className="flex gap-2">
                <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                <span>{caveat}</span>
              </div>
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
}

function KpiResult({
  format,
  rows,
  seriesLabel,
  valueKey,
}: {
  format?: 'currency' | 'number' | 'percent';
  rows: Array<Record<string, string | number | boolean | null>>;
  seriesLabel: string;
  valueKey: string;
}) {
  const value = getKpiValue(rows, valueKey, seriesLabel);
  return (
    <div className="rounded-md bg-paper-light px-4 py-5">
      <div className="text-xs font-medium uppercase tracking-wide text-ink-muted">{seriesLabel}</div>
      <div className="mt-2 text-3xl font-semibold leading-none text-ink">{formatDisplayValue(value, format)}</div>
    </div>
  );
}

function getKpiValue(
  rows: Array<Record<string, string | number | boolean | null>>,
  valueKey: string,
  seriesLabel: string,
) {
  const isAverageMetric = seriesLabel.toLowerCase().includes('average');
  const hasAmountAndCount = rows.some((row) => row.amount !== undefined && row.count !== undefined);
  if (isAverageMetric && hasAmountAndCount) {
    let totalAmount = 0;
    let totalCount = 0;
    for (const row of rows) {
      totalAmount += toNumber(row.amount);
      totalCount += toNumber(row.count);
    }
    return totalCount > 0 ? totalAmount / totalCount : 0;
  }

  return rows.reduce((sum, row) => sum + toNumber(row[valueKey]), 0);
}

function AnalyticsRowsTable({
  rows,
  seriesList,
  xKey,
}: {
  rows: Array<Record<string, string | number | boolean | null>>;
  seriesList: Array<{
    format?: 'currency' | 'number' | 'percent';
    key?: string;
    label?: string;
  }>;
  xKey: string;
}) {
  const seriesKeys = new Set(seriesList.map((item) => item.key || 'value'));
  const primaryKey = seriesList[0]?.key || 'value';
  const hasShare = rows.some((row) => row.share !== undefined && row.share !== null);
  const hasAmount = rows.some((row) => row.amount !== undefined && row.amount !== null);
  const shouldShowAmount =
    hasAmount &&
    !seriesKeys.has('amount') &&
    !rows.every((row) => toNumber(row.amount) === toNumber(row[primaryKey]));
  const shouldShowCount =
    rows.some((row) => row.count !== undefined && row.count !== null) &&
    !seriesKeys.has('count') &&
    !rows.every((row) => toNumber(row.count) === toNumber(row[primaryKey]));
  const shouldShowTotalPayments =
    rows.some((row) => row.totalPayments !== undefined && row.totalPayments !== null) &&
    !seriesKeys.has('totalPayments');
  const shouldShowEligible =
    rows.some((row) => row.eligiblePayments !== undefined && row.eligiblePayments !== null) &&
    !seriesKeys.has('eligiblePayments');
  const hasExtraMetrics = hasShare || shouldShowAmount || shouldShowCount || shouldShowTotalPayments || shouldShowEligible;
  return (
    <div className="max-h-56 overflow-auto rounded-md border border-edge/70">
      <table className={`w-full text-left text-xs ${seriesList.length > 1 || hasExtraMetrics ? 'min-w-[480px]' : 'table-fixed'}`}>
        <thead className="sticky top-0 bg-paper-light text-ink-muted">
          <tr>
            <th className="sticky left-0 z-10 bg-paper-light px-3 py-2 font-medium">Segment</th>
            {seriesList.map((item) => (
              <th key={item.key || 'value'} className="px-3 py-2 text-right font-medium">
                {item.label || 'Value'}
              </th>
            ))}
            {shouldShowAmount ? <th className="px-3 py-2 text-right font-medium">Amount</th> : null}
            {hasShare ? <th className="px-3 py-2 text-right font-medium">Share</th> : null}
            {shouldShowCount ? <th className="px-3 py-2 text-right font-medium">Count</th> : null}
            {shouldShowTotalPayments ? <th className="px-3 py-2 text-right font-medium">Payments</th> : null}
            {shouldShowEligible ? <th className="px-3 py-2 text-right font-medium">Eligible</th> : null}
          </tr>
        </thead>
        <tbody className="divide-y divide-edge/60 bg-white">
          {rows.map((row, index) => (
            <tr key={`${String(row[xKey] || row.name || row.period || index)}-${index}`}>
              <td className="sticky left-0 max-w-[180px] break-words bg-white px-3 py-2 text-ink">
                {String(row[xKey] || row.name || row.period || 'Result')}
              </td>
              {seriesList.map((item) => {
                const key = item.key || 'value';
                return (
                  <td key={key} className="whitespace-nowrap px-3 py-2 text-right tabular-nums text-ink">
                    {formatDisplayValue(toNumber(row[key]), item.format)}
                  </td>
                );
              })}
              {shouldShowAmount ? (
                <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums text-ink-muted">
                  {formatDisplayValue(toNumber(row.amount), 'currency')}
                </td>
              ) : null}
              {hasShare ? (
                <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums text-ink-muted">
                  {row.share === undefined || row.share === null
                    ? '-'
                    : formatDisplayValue(toNumber(row.share), 'percent')}
                </td>
              ) : null}
              {shouldShowCount ? (
                <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums text-ink-muted">
                  {formatDisplayValue(toNumber(row.count), 'number')}
                </td>
              ) : null}
              {shouldShowTotalPayments ? (
                <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums text-ink-muted">
                  {formatDisplayValue(toNumber(row.totalPayments), 'number')}
                </td>
              ) : null}
              {shouldShowEligible ? (
                <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums text-ink-muted">
                  {formatDisplayValue(toNumber(row.eligiblePayments), 'number')}
                </td>
              ) : null}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function formatTooltipValue(value: unknown, format?: 'currency' | 'number' | 'percent') {
  return formatDisplayValue(toNumber(value), format);
}

function formatAxisValue(value: unknown, format?: 'currency' | 'number' | 'percent') {
  const numeric = toNumber(value);
  if (format === 'currency') {
    return compactCurrencyFormatter.format(numeric);
  }
  if (format === 'percent') {
    return `${numeric}%`;
  }
  return compactNumberFormatter.format(numeric);
}

function formatDisplayValue(value: number, format?: 'currency' | 'number' | 'percent') {
  if (format === 'currency') {
    return currencyFormatter.format(value);
  }
  if (format === 'percent') {
    return `${value.toLocaleString('en-US', { maximumFractionDigits: 1 })}%`;
  }
  return numberFormatter.format(value);
}

function toNumber(value: unknown) {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : 0;
}

const currencyFormatter = new Intl.NumberFormat('en-US', {
  currency: 'USD',
  maximumFractionDigits: 2,
  style: 'currency',
});
const compactCurrencyFormatter = new Intl.NumberFormat('en-US', {
  currency: 'USD',
  maximumFractionDigits: 0,
  notation: 'compact',
  style: 'currency',
});
const numberFormatter = new Intl.NumberFormat('en-US');
const compactNumberFormatter = new Intl.NumberFormat('en-US', {
  maximumFractionDigits: 1,
  notation: 'compact',
});
